/**
 * TopBar - app-wide top bar for the application shell.
 *
 * Title resolution (in priority order):
 *   1. Explicit page title via useSetTopBarTitle() (pages we own set this).
 *   2. Fallback route title via useRouteTitle() (covers pages we DON'T own,
 *      like Backstage Settings/Catalog/Docs, with no per-page wiring).
 *   3. Nothing -> left side empty, actions stay right.
 */
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import SettingsIcon from '@material-ui/icons/Settings';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import PersonIcon from '@material-ui/icons/Person';
import { LanguageSwitcherToggle } from '../LanguageSwitcher';
import { useControlStyles } from './useControlStyles';
import { useTopBarTitleValue } from './TopBarTitleContext';
import { useRouteTitle } from './useRouteTitle';
import { useTranslation } from 'react-i18next';
import { logout } from '../../utils/apiClient';

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    minHeight: 64,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  titleWrap: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
    gap: 2,
  },
  title: {
    fontSize: '1.05rem',
    fontWeight: 700,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  subtitle: {
    fontSize: '0.78rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.35,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  spacer: { flex: 1, minWidth: theme.spacing(2) },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    flexShrink: 0,
  },
  logoutText: { [theme.breakpoints.down('xs')]: { display: 'none' } },
  roleText: { [theme.breakpoints.down('sm')]: { display: 'none' } },
  badgeDetail: {
    display: 'block',
    fontSize: '0.68rem',
    fontWeight: 400,
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: { display: 'none' },
  },
}));

type Props = {
  /** Display-only role label shown in the badge. */
  roleLabel?: string;
  onLogout?: () => void;
  user?: { name: string; email?: string };
  logoutLabel?: string;
  logoutAriaLabel?: string;
  showSettings?: boolean;
  onSettings?: () => void;
};

export const TopBar = ({
  roleLabel,
  onLogout,
  user,
  logoutLabel,
  logoutAriaLabel,
  showSettings = true,
  onSettings,
}: Props) => {
  const classes = useStyles();
  const ctrl = useControlStyles();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('xs'));
  const { t } = useTranslation('shell');
  // The user's name (or email, when the profile has no name) over their role.
  const badgeTitle = user?.name || user?.email || roleLabel;
  const badgeDetail = user
    ? roleLabel ?? (user.name ? user.email : undefined)
    : undefined;

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
      return;
    }

    logout();
  };

  // 1) explicit page title, else 2) route fallback.
  const explicit = useTopBarTitleValue();
  const routeTitle = useRouteTitle();
  const title = explicit.title || routeTitle?.title;
  const subtitle = explicit.subtitle || routeTitle?.subtitle;

  return (
    <Toolbar className={classes.toolbar} component="header">
      {/* Dynamic Title and subtitle based on the explicit set value or route title */}
      {(title || subtitle) && (
        <div className={classes.titleWrap}>
          {title && (
            <Typography component="h1" className={classes.title}>
              {title}
            </Typography>
          )}
          {subtitle && <span className={classes.subtitle}>{subtitle}</span>}
        </div>
      )}

      <span className={classes.spacer} />

      <div className={classes.actions}>
        <LanguageSwitcherToggle iconOnly={isNarrow} />

        {/* Shown only when the page knows who or what role to display. */}
        {badgeTitle && (
          <span className={`${ctrl.control} ${ctrl.readonly}`}>
            <PersonIcon className={ctrl.iconAccent} aria-hidden="true" />
            <span className={classes.roleText}>
              {badgeTitle}
              {badgeDetail && (
                <span className={classes.badgeDetail}>{badgeDetail}</span>
              )}
            </span>
          </span>
        )}

        {showSettings && (
          <Tooltip title={t('settings')}>
            <button
              type="button"
              className={ctrl.control}
              aria-label={t('openSettings')}
              onClick={onSettings}
            >
              <SettingsIcon className={ctrl.icon} />
            </button>
          </Tooltip>
        )}

        {/* Clear the backend session and return to the product login page. */}
        <Tooltip title={t('logout')}>
          <button
            type="button"
            className={`${ctrl.control} ${ctrl.logoutBtn}`}
            aria-label={logoutAriaLabel ?? t('logout')}
            onClick={handleLogout}
          >
            <ExitToAppIcon className={ctrl.icon} />
            <span className={classes.logoutText}>
              {logoutLabel ?? t('logoutButton')}
            </span>
          </button>
        </Tooltip>
      </div>
    </Toolbar>
  );
};
